/* jshint evil:true */

// "@babel/polyfill" supports ES6 features like Object.assign, Promise, etc. for older browsers like IE11
import "@babel/polyfill";

// supports nodeList.forEach() for older browsers like IE11
import "nodelist-foreach-polyfill";

// Supports ":scope" query selector.
// This is required when using selectors like "> button" which is not allowed in querySelector() function.
// Correct is: ":scope > button" which requires this polyfill for IE11.
import "element-qsa-scope";

// supports custom elements for older browsers like IE11
// Temporarily disabled because this won't work in Apple Safari browsers
// import "@webcomponents/webcomponentsjs";

import i18next from "i18next";
import i18nextBackend from "i18next-xhr-backend";
import "string.format";
import React from "react";
import ReactDOM from "react-dom";
import utils from "./tools/utilities";
import HcsUiManager from "./uimanager";
import HcsApi from "./api";
import HcsCad from "./cad";
import HcsPartsList from "./partslist";
import HcsCart from "./cart";
import HcsDebugWindow from "./hcsdebugwindow";
import HcsConfigurator from "./logic/hcs-configurator";
import MessageBox from "./messagebox";
import TrackingBase from "./tools/tracking-base";
import GoogleAnalytics from "./tools/tracking-google-analytics";
import urlJoin from "url-join";
import { HcsRatingButton } from "./controls/hcs-rating-control";

window.cadclick = window.cadclick || {
    loader: null,
    debugWindow: null,
    version: "639293428531217288",
    hcs: {}
};

// Avoid "process is not defined" startup errors because this property is only
// available in "node" environment not in .NET web applications
window.process = {
    env: {
        NODE_ENV: 'development'
    }
};

/**
 * @class
 * @classdesc Main loader class which is the entry point for starting a new configurator inside an existing web page.
 * <br>
 * Create a DIV element and include the JavaScript file for loading the API. The script URL requires a "callback" parameter
 * which specifies an existing function which will be called when the API script has been successfully loaded.
 * In this function call the APIs init function where you have to specify the ID of the container and the name of the configurator.
 * <br>
 * After initialization (see {@link HcsLoader#init}) you have access to HCS API functions using the following reference: window.cadclick.hcs[&lt;name of configurator&gt;].
 * @example
 * <div id="dress-code"></div>
 * 
 * <script type="text/javascript">
 *      function hcsSuccess(arg) {
 *           if(arg.success) {
 *               this.init({
 *                   containerId: "dress-code",
 *                   configurationIdType: "Default",
 *                   configurator: "dress_code",
 *                   cultureId: "de-DE",
 *                   sectionId: "HDE",
 *                   configurationId: "xxxx-xxxx-xxxxx",
 *                   availablePriceTypes: ["SALES_PRICE", "CUSTOMER_PRICE", "UVPE"],
 *                   selectedPriceType: "SALES_PRICE",
 *                   currency: "EUR",
 *                   isAuthenticated: true,
 *                   customerNo: "1047378",
 *                   ready: function() {
 *
 *                   }
 *               });
 *           }
 *       }
 * </script>
 * 
 * <script src="../api/hcs?callback=hcsSuccess" type="text/javascript" async></script>
 */
class HcsLoader
{
    constructor() {
        this.getUrlParameterByName = utils.getUrlParameterByName;
        this.getUrlParameters = utils.getUrlParameters;
        this.parseBoolean = utils.parseBoolean;

        this.constants = {
            appUrl: "https://demo.cadclick.com/haefele/dresscode",
            scriptUrl: "https://demo.cadclick.com/haefele/dresscode/js",
            styleUrl: "https://demo.cadclick.com/haefele/dresscode/css",
            versionUid: "639293428531217288",
            debug: this.parseBoolean("true"),
            minified: this.parseBoolean("false"),
            hcsSuccessOverride: this.parseBoolean("1"),
            enums: {
                configurationIdTypes: JSON.parse("{ "Default": 0, "Short": 1, "Shop": 2, "New": -1 }"),
                configurationStates: JSON.parse("{ "NEW": 0, "SAVED": 1, "EDIT": 2, "UNUSED": 3, "DELETED": 4, "CHECK": 5 }"),
                sceneVisibilityModes: JSON.parse("{ "AlwaysOff": 0, "AlwaysOn": 1, "InitiallyOff": 2, "InitiallyOn": 3 }"),
            },
            translations: JSON.parse('{"ErrorMessageDialog_Title": "Fehler", "ApiLoader_Settings_CultureNotFound": "Leider ist der Konfigurator in dieser Landes- und Sprachauswahl nicht verfügbar. Bitte kontaktieren Sie Ihr zuständiges Häfele Verkaufsbüro.", "ErrorMessageDialog_ButtonReloadPage": "Seite neu laden", "OK": "OK"}'),
            callbackInitFunction: null,
            showDebugWindow: this.parseBoolean(this.getUrlParameterByName("hcs-debug"))
        };

        this.baseCssLoaded = false;

        try {
            var ndDoctype = document.doctype;
            var docTypeString = "<!DOCTYPE " + ndDoctype.name + (ndDoctype.publicId ? ' PUBLIC"' + ndDoctype.publicId + '"' : '') + (!ndDoctype.publicId && ndDoctype.systemId ? ' SYSTEM' : '') + (ndDoctype.systemId ? ' "' + ndDoctype.systemId + '"' : '') + ">";

            // HcS requires HTML5 documents, otherwise UI issues can appear.
            if(docTypeString !== "<!DOCTYPE html>") {
                console.warn("Document is not HTML5 compliant. This can lead to UI issues in HcS configurator. It's recommended to set document type to HTML5!");
            }

            if(this.constants.hcsSuccessOverride) {
                if(!window.configuratorConfiguration) {
                    console.error("No 'window.configuratorConfiguration' object found!");
                    return;
                }

                let extConfigurator = Object.keys(window.configuratorConfiguration)[0];

                if(!extConfigurator) {
                    console.error("No configurator found in: window.configuratorConfiguration!");
                    return;
                }

                let extSectionId = window.configuratorConfiguration[extConfigurator].channel;

                if(extSectionId === "" && window.locale_information) {
                    extSectionId = window.locale_information.requestSiteId;
                }

                this.init({
                    containerId: window.configuratorConfiguration[extConfigurator].divId,
                    configurator: extConfigurator,
                    cultureId: window.configuratorConfiguration[extConfigurator].locale.replace("_", "-"),
                    sectionId: extSectionId,
                    configurationId: window.configuratorConfiguration[extConfigurator].configurationId,
                    configurationIdType: window.configuratorConfiguration[extConfigurator].configurationIdType,
                    availablePriceTypes: window.configuratorConfiguration[extConfigurator].availablePriceTypes,
                    selectedPriceType: window.configuratorConfiguration[extConfigurator].selectedPriceType,
                    currency: window.configuratorConfiguration[extConfigurator].currency,
                    isAuthenticated: window.configuratorConfiguration[extConfigurator].isAuthenticated
                });
            } else {
                this.callbackInitFunction = eval("");

                this.callbackInitFunction.call(this, {
                    success: true,
                    error: ""
                });
            }
        } catch(e) {
            if(typeof this.callbackInitFunction === "undefined") {
                console.error(`Loader script finished, but could not find caller function: '': ${e.message}`);
            } else {
                console.error(`Error in caller function '': ${e.message}`);
            }
            this.callbackInitFunction = null;
        }
    }

    /**
     * Loads one or more JavaScript files.
     * @param {string|Array} scriptList - Name of a single JavaScript file or array of multiple files.
     * @param {Object} [options] - Additional options
     * @param {Object} [options.success] - Callback function after all scripts have been successfully loaded.
     * @param {Object} [options.error] - Callback function when loading a script has failed.
     */
    loadScripts(scriptList, options) {
        var args = arguments;
        var self = this;

        var opts = {
            success: args.length > 1 && typeof options.success !== "undefined" ? options.success : function() { },
            error: args.length > 1 && typeof options.error !== "undefined" ? options.error : function(args) { console.error(`Error loading script: ${args.script}`); }
        };

        if(typeof scriptList === "string") {
            scriptList = [scriptList];
        }

        var scriptsLoaded = 0;

        var loadComplete = function() {
            scriptsLoaded++;

            if(scriptsLoaded >= scriptList.length) {
                opts.success.call(self, {});
            }
        };

        scriptList.forEach((scriptName) => {
            var scriptTag = document.createElement("script");
            scriptTag.setAttribute("type", "text/javascript");
            scriptTag.setAttribute("async", "async");
            scriptTag.setAttribute("src", this.constants.appUrl + "/" + scriptName + "?v=" + this.constants.versionUid);
            scriptTag.addEventListener("error", function(error) {
                opts.error.call(self, { script: scriptName });
            });

            if(scriptTag.readyState) {
                scriptTag.onreadystatechange = function() {
                    if(this.readyState === "complete" || this.readyState === "loaded") {
                        loadComplete();
                    }
                };
            } else {
                scriptTag.onload = loadComplete;
            }

            (document.getElementsByTagName("head")[0] || document.documentElement).appendChild(scriptTag);
        });
    }

    /**
     * Load a CSS file.
     * @param {string} cssName - Name of the CSS file. The name must be relative to the root CSS folder (wwwroot\css).
     * The complete URL will be createdd internally.
     * @param {string} root - Define root folder where to load CSS files. Valid values are:
     * "css" - Default CSS folder inside wwwroot
     * "cfg" - Configurator CSS folder: wwwroot/ProjectFiles
     */
    loadCss(cssName, root) {
        var link = document.createElement("link");
        var cssRoot = "";

        switch(root) {
            case "css":
                cssRoot = this.constants.styleUrl;
                break;

            case "cfg":
                cssRoot = urlJoin(this.constants.appUrl, "ProjectFiles");
                break;

            default:
                console.error("hcs.loadCss: Unsupported CSS root definition in function call: " + root);
                break;
        }

        link.type = "text/css";
        link.rel = "stylesheet";
        link.href = cssRoot + "/" + cssName + "?v=" + this.constants.versionUid;
        (document.getElementsByTagName("head")[0] || document.documentElement).appendChild(link);
    }

    /**
     * Takes a given text and replaces all newline characters with a HTML newline tag.
     * @param {string} text - Text with newline characters.
     * @returns {string} Given text ready for display in HTML where all newline character have been replace by HTML newlines.
     */
    replaceNewlines(text) {
        return text.replace(/(?:\r\n|\r|\n)/g, "<br />");
    }

    /**
     * Create simple HTML code for error messages which can be added to existing containers.
     * @param {string} message - Error message
     * @param {string} [details] - Error message details (stack trace, ...)
     * @returns {string} HTML code for error message.
     */
    createErrorHtml(message, details) {
        return `<svg version="1.1" xmlns="http://www.w3.org/2000/svg">
            <div style="width: 0; height: 0; overflow: hidden">
                <svg version="1.1" xmlns="http://www.w3.org/2000/svg">
                    <symbol xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" id="loader-dialog-error">
                        <path d="M5.71,54.86H58.29L32,9.14Zm7.78-4.12L32,16.46,50.74,50.74Z"/>
                        <path d="M32,42.29A3,3,0,0,0,29,45a3.19,3.19,0,0,0,3,3,2.83,2.83,0,0,0,2.74-3A2.64,2.64,0,0,0,32,42.29Z"/>
                        <path d="M29,29.26c0,1.6,3,11.43,3,11.43s2.74-9.83,2.74-11.43A2.64,2.64,0,0,0,32,26.51,3,3,0,0,0,29,29.26Z"/>
                    </symbol>
                </svg>
            </div>
        </svg>
        
        <div class="hcs-msg-box hcs-show hcs-error hcs-msg-box-loader">
            <h1 class="hcs-title">
                <svg icon-id="loader-dialog-error" version="2.0">
                    <use href="#loader-dialog-error"></use>
                </svg>
                ${this.constants.translations.ErrorMessageDialog_Title}
            </h1>
            <div class="hcs-message">
                <span class="hcs-icon">
                    <svg icon-id="loader-dialog-error" version="2.0">
                        <use href="#loader-dialog-error"></use>
                    </svg>
                </span>
                <div class="hcs-message-text">
                    <div class="hcs-msg-main">${this.replaceNewlines(message)}</div>
                    <div class="hcs-msg-details">${this.replaceNewlines(details || "")}</div>
                </div>
            </div>
            <div class="hcs-buttons">
                <button onclick="document.querySelector('.hcs-msg-box-loader').style.display='none'" class="hcs-button hcs-visible cancel hcs-tertiary">${this.constants.translations.OK}</button></div>
            </div>
        </div>`;
    }

    /**
     * Load correct URL for ccAO.
     * When hosting page already provides a jQuery library we can load ccAO with integrated jQuery.
     * @param {string} libFilename - Name of ccAO JavaScript file to be loaded.
     * @returns {string} ccAO library JavaScript relative path.
     */
    ccAssemblerOnlineLib(libFilename) {
        if(!libFilename || libFilename === "") {
            throw new Error("Could not load ccAO because no version is specified in 'SETTINGS.CCAO_SCRIPT_NAME'");
        }

        return "ccAO/" + libFilename;
    }

    /**
     * Call external function which is outside our HCS scope.
     * Those functions are defined in settings database.
     * It's required that the function definition is as defined in the example below.
     * "name" is the name of the function to be called.
     * "args" defines the function call arguments. All arguments will be evaluated.
     * Static values are also possible, strings must be extra-quoted.
     * The variable "$$CFG_NAME$$" can be used either in function name and arguments
     * and will be translated to the current configurator name.
     * When function returns a promise object you can define the "then" and "catch"
     * calls since some external functions use other functions than the default ones.
     * @example
     * {
     *    "name": "myFunctionCall",
     *    "args": [
     *       "'myStaticValue'",
     *       "window.cadclick.hcs.myProperty"
     *    ],
     *    "then": "then",
     *    "fail": "fail"
     * }
     * @param {string} configurator - Name of the configurator
     * @param {string} functionPropertyName - Name of the settings entry which contains the function definition.
     * @param {...any} funcArgs - Additional arguments which are being passed to the function itself.
     * When the function definition contains any arguments the "funcArgs" will be added behind those.
     * @returns {Promise} Promise object from function.
     */
    externalFunction(configurator, functionPropertyName, ...funcArgs) {
        return new Promise((resolve, reject) => {
            try {
                let functionDef = window.cadclick.hcs[configurator].settings.get(functionPropertyName, "json");

                let functionCall = eval(functionDef.name.replace("$$CFG_NAME$$", configurator));

                if(typeof (functionCall) !== "function") {
                    throw functionDef.name + " is not a function!";
                }

                let functionArgs = [];

                if(functionDef.args) {
                    functionArgs = functionDef.args.map(arg => {
                        return eval(arg.replace("$$CFG_NAME$$", configurator));
                    });
                }
                
                functionArgs = functionArgs.concat(funcArgs);

                let functionComplete = "functionCall.apply(null, functionArgs)";

                if(functionDef.then) {
                    functionComplete += `.${functionDef.then}(function() {
                        resolve.apply(null, arguments);
                    })`;
                }

                if(functionDef.fail) {
                    functionComplete += `.${functionDef.fail}(function() {
                        reject.apply(null, arguments);
                    })`;
                }

                eval(functionComplete);
            } catch(e) {
                reject({
                    message: `Could not evaluate function as defined in property ${functionPropertyName}: ` + e.message,
                    stack: e.stack
                });
            }
        });
    }

    /**
     * Start a new configurator.
     * @param {object} options - Init options
     * @param {string} options.containerId - ID of the container inside HTML DOM where to render the configurator UI.
     * @param {string} options.configurator - Name (internal ID) of the configurator (dress_code, ...).
     * @param {string} options.sectionId - Section ID or department (HDE, ...)
     * @param {string} options.cultureId - Culture in which configurator shall be displayed. Valid cultures are like: de-DE, en-US, ...
     * The given culture must be defined in configurator dictionary, otherwise it will fallback to the alternative culture.
     * @param {string} options.configurationId - ID to start a new or exisitng configuration. When the given ID already exists in database we load it.
     * Otherwise we start a new configuration from scratch. When the URL contains a "hcs-load-configuration" parameter with an existing ID we always
     * load this configuration.
     * @param {array} options.availablePriceTypes - List of available price types (SALES_PRICE, CUSTOMER_PRICE, ...) which populates the price type selector.
     * @param {string} options.selectedPriceType - Initially selected price type. This must be one of the values as defined in "availablePriceTypes".
     * @param {string} options.currency - User's currency (EUR, ...)
     * @param {bool} [options.isAuthenticated=false] - Control configurator behavior depending on wether a user is logged-in or not. Set "true" when user is logged-in.
     * @param {string} [options.customerNo] - User ID or customer number. This value will only be used for statistical purposes.
     * @param {bool} [options.outsideScrollLock='SETTINGS.UI_OUTSIDE_SCROLL_LOCK'] - When the HTML page is larger than the browser window the browser will display a scrollbar.
     * When scrolling inside the configurator (using mouse wheel) the entire page will scroll up or down and the configurator will scroll out of focus.
     * With this option enabled the configurator will disable the outer scrollbar as long as the mouse cursor is inside the configurator area.
     * When this option is not set the default value from "SETTINGS.UI_OUTSIDE_SCROLL_LOCK" will be used.
     * @param {function} options.ready - Callback function which indicates that the configurator has been successfully initialized.
     */
    init(options) {
        let container = null;
        let self = this;

        var opts = Object.assign({}, {
            containerId: null,
            configurator: null,
            sectionId: null,
            cultureId: null,
            configurationId: null,
            configurationIdType: "Default",
            availablePriceTypes: [],
            selectedPriceType: null,
            currency: "???",
            isAuthenticated: false,
            customerNo: null,
            outsideScrollLock: null,
            ready: function() { }
        }, options);

        opts.isAuthenticated = opts.isAuthenticated || this.getUrlParameterByName("hcs-override-authentication") === "1";

        // Load configuration via URL parameter (also used for short links)
        // This will always override the ID from init() call
        let urlCfgId = this.getUrlParameterByName("hcs-load-configuration");
        let urlCfgType = this.getUrlParameterByName("hcs-configuration-id-type");

        // Loading configurations using the default configuraton ID (not shop or short ID)
        // this is only allowed for restoring user sessions from local storage.
        // To load this configuration form database anyway set this URL parameter to "1".
        let loadCfgForce = this.getUrlParameterByName("hcs-force-load") === "1";

        if(urlCfgId) {
            opts.configurationId = urlCfgId;

            if(urlCfgType) {
                opts.configurationIdType = urlCfgType;
            }
        }

        var cfgId = opts.configurator.toLowerCase();

        if(window.cadclick.hcs[cfgId]) {
            console.error(`Could not initialize HCS configurator '${cfgId}': Configurator already exists!`);
            return;
        }

        window.cadclick.hcs[cfgId] = {
            settings: {}
        };

        if(!this.baseCssLoaded) {
            // Load basic CSS file
            this.loadCss(this.constants.minified ? "hcs.min.css" : "hcs.css", "css");
            this.baseCssLoaded = true;
        }

        container = document.getElementById(opts.containerId);

        if(container !== null) {
            container.classList.add("hcs", "hcs-cul-" + opts.cultureId);

            fetch(this.constants.appUrl + "/" + opts.configurator + "/settings/" + opts.cultureId)
                .then(response => response.json())
                .then(settingsData => {
                    if(settingsData.success) {
                        window.cadclick.hcs[opts.configurator].settings = settingsData.items;

                        /**
                         * Allow reading application settings via window.cadclick.hcs[cfgid].settings.get().
                         * Since all settings are stored as varchars in database the desired data type must
                         * be defined while reading. Therefore the get() function allows to set the desired
                         * data type. When not specified the value will be returned as string.
                         * @param {string} propertyName - Name of the property to be read. Requires parameter section
                         * and name, like "SETTINGS.CAD_SERVER".
                         * @param {string} [dataType=string] - Desired output data type which converts the (stringified) value from
                         * database into the target type where "string" is the default return value.
                         * @param {string} [nullIfEmpty=false] - When desired parameter could not be found or value is empty
                         * it'll writes a console error. With this value "true" it returs "null" in this case.
                         * @returns {any} Value of settings parameter.
                         */
                        window.cadclick.hcs[cfgId].settings.get = function(propertyName, dataType = "string", nullIfEmpty = false) {
                            let value = window.cadclick.hcs[cfgId].settings[propertyName];

                            if(typeof (value) !== "undefined") {
                                switch(dataType) {
                                    case "int":
                                    case "integer":
                                        return parseInt(value);

                                    case "float":
                                        return parseFloat(value);

                                    case "bool":
                                    case "boolean":
                                        return self.parseBoolean(value);

                                    case "json":
                                        try {
                                            return JSON.parse(value);
                                        } catch(e) {
                                            console.error(`Could not parse property '${propertyName}' as JSON: ${e.message}`);
                                            return undefined;
                                        }

                                    case "string":
                                        return value;

                                    default:
                                        console.warn(`settings.get('${propertyName}'): Unsupported data type: ${dataType}`);
                                        return value;
                                }
                            } else {
                                if(nullIfEmpty) {
                                    return null;
                                }

                                //console.warn("Undefined setting: " + propertyName);
                            }

                            return undefined;
                        };
                    } else {
                        container.innerHTML = self.createErrorHtml(settingsData.error.message);
                        return;
                    }

                    i18next
                        .use(i18nextBackend)
                        .init({
                            load: "currentOnly",
                            lng: opts.cultureId,
                            fallbackLng: window.cadclick.hcs[cfgId].settings.get("SETTINGS.DEFAULT_CULTURE_ID"),
                            ns: [],
                            debug: self.debug,
                            backend: {
                                loadPath: self.constants.appUrl + "/translations/{{lng}}/{{ns}}/" + opts.configurator + "?v=" + this.constants.versionUid,
                                crossDomain: true
                            }
                        }, () => {
                            let appConfigId = window.cadclick.hcs[cfgId].settings.get("SETTINGS.APP_CONFIGURATION_ID");
                            let sceneVisibilityMode = window.cadclick.hcs[cfgId].settings.get("SETTINGS.SCENE_VISIBILITY", "int");

                            if(appConfigId && appConfigId !== "") {
                                container.classList.add("hcs-app-config-" + appConfigId.toLowerCase());
                            }

                            if([0, 2].includes(sceneVisibilityMode)) {
                                container.classList.add("hcs-scene-hidden");
                            }

                            i18next.loadNamespaces(["GUI", "ECS", "CONFIGURATOR"], function(errors, t) {
                                if(errors && errors.length > 0) {
                                    errors.forEach((error) => {
                                        console.error("Could not load translations: " + error);
                                    });
                                }

                                if(opts.isAuthenticated) {
                                    container.classList.add("hcs-login");
                                } else {
                                    container.classList.remove("hcs-login");
                                }

                                fetch(self.constants.appUrl + "/Inline/HcS/" + opts.configurator + "/" + opts.sectionId + "/" + opts.cultureId + "/?v=" + self.constants.versionUid + "&debug=" + (self.constants.showDebugWindow ? "1" : "0"))
                                    .then(res => res.json())
                                    .then((inlineResult) => {
                                        if(inlineResult.success) {
                                            container.innerHTML = inlineResult.contentHtml;

                                            fetch(self.constants.appUrl + "/svg-icons?v=" + self.constants.versionUid)
                                                .then(res => res.text())
                                                .then(svgIcons => {
                                                    container.innerHTML += "<div style=\"display: none; width: 0; height: 0; overflow: hidden\">" +  svgIcons + "</div>";
                                                });

                                            self.loadScripts([
                                                `api/${opts.configurator}`,
                                                self.ccAssemblerOnlineLib(window.cadclick.hcs[cfgId].settings.get("SETTINGS.CCAO_SCRIPT_NAME"))
                                            ], {
                                                success: function() {
                                                    self.loadCss(`${cfgId}/css/entry${self.constants.minified ? ".min" : ""}.css`, "cfg");

                                                    let ecsSectionMappings = window.cadclick.hcs[cfgId].settings.get("ECS_ENGINE.SECTION_MAPPING", "json");
                                                    let ecsSectionId = opts.sectionId;

                                                    if(ecsSectionMappings && ecsSectionMappings[opts.sectionId]) {
                                                        ecsSectionId = ecsSectionMappings[opts.sectionId];
                                                    }

                                                    window.cadclick.hcs[cfgId] = Object.assign(window.cadclick.hcs[cfgId], self, {
                                                        t: {
                                                            i18next: i18next,
                                                            gui: i18next.getFixedT(null, "GUI"),
                                                            ecs: i18next.getFixedT(null, "ECS"),
                                                            cfg: i18next.getFixedT(null, "CONFIGURATOR")
                                                        },
                                                        container: container,
                                                        configurator: cfgId,
                                                        sectionId: opts.sectionId,
                                                        ecsSectionId: ecsSectionId,
                                                        cultureId: opts.cultureId,
                                                        userId: opts.customerNo,
                                                        availablePriceTypes: opts.availablePriceTypes,
                                                        selectedPriceType: opts.selectedPriceType,
                                                        currency: opts.currency,
                                                        isAuthenticated: opts.isAuthenticated,
                                                        externalFunction: (functionPropertyName, ...funcArgs) => {
                                                            return self.externalFunction.apply(null, [cfgId, functionPropertyName].concat(funcArgs));
                                                        },
                                                        onRenderFinished: () => {
                                                            window.cadclick.hcs[cfgId].ui.navScrollbarInit();
                                                            window.cadclick.hcs[cfgId].ui.configScrollbarInit();
                                                        }
                                                    });

                                                    window.cadclick.hcs[cfgId].api = new HcsApi({
                                                        hcs: window.cadclick.hcs[cfgId]
                                                    });

                                                    window.cadclick.hcs[cfgId].ui = new HcsUiManager({
                                                        container: container,
                                                        outsideScrollLock: opts.outsideScrollLock || window.cadclick.hcs[cfgId].settings.get("SETTINGS.UI_OUTSIDE_SCROLL_LOCK", "bool"),
                                                        hcs: window.cadclick.hcs[cfgId]
                                                    });

                                                    if(window.cadclick.hcs[cfgId].settings.get("SETTINGS.RATING_BUTTON_ENABLED", "bool")) {
                                                        ReactDOM.render(React.createElement(HcsRatingButton, {
                                                            hcs: window.cadclick.hcs[cfgId]
                                                        }), container.querySelector(".hcs-rating-button-container"));
                                                    }

                                                    Object.assign(window.cadclick.hcs[cfgId], window.cadclick.hcs[cfgId].api);

                                                    window.cadclick.debug = new HcsDebugWindow({
                                                        hcs: window.cadclick.hcs[cfgId],
                                                        enabled: self.constants.showDebugWindow,
                                                        containerId: "hcs-debug",
                                                        identSelected: (ident) => {
                                                            let selectCurrent = container.querySelector(`.hcs-wrapper li[ecs-key].hcs-highlight`);
                                                            let selectItemNew = container.querySelector(`.hcs-wrapper li[ecs-key='${ident}']`);

                                                            if(selectCurrent !== null) {
                                                                selectCurrent.classList.remove("hcs-highlight");
                                                            }

                                                            if(selectItemNew !== null) {
                                                                selectItemNew.classList.add("hcs-highlight");
                                                            }

                                                            return selectItemNew !== null;
                                                        }
                                                    });

                                                    window.cadclick.debug.console.info("HCS version info" +
                                                        window.cadclick.debug.console.table([
                                                            { "HCS version:": window.cadclick.hcs[cfgId].settings.get("SETTINGS.BUILD_VERSION") },
                                                            { "DB version:": window.cadclick.hcs[cfgId].settings.get("VERSION.DATABASE") },
                                                            { "CFG version:": window.cadclick.hcs[cfgId].settings.get("VERSION.CONFIGURATOR") },
                                                            { "Env:": window.cadclick.hcs[cfgId].api.emptyReplace(window.cadclick.hcs[cfgId].settings.get("SETTINGS.APP_CONFIGURATION_ID"), "-") }
                                                        ])
                                                    );

                                                    if(this.constants.hcsSuccessOverride && window.configuratorConfiguration) {
                                                        window.cadclick.debug.console.debug("Shop data", window.configuratorConfiguration);
                                                    }

                                                    window.cadclick.hcs[cfgId].cad = new HcsCad({
                                                        hcs: window.cadclick.hcs[cfgId]
                                                    });

                                                    // Build UI with react renderer
                                                    window.cadclick.hcs[cfgId].renderer = ReactDOM.render(
                                                        React.createElement(HcsConfigurator, { hcs: window.cadclick.hcs[cfgId] }),
                                                        container.querySelector(".hcs-config-area")
                                                    );

                                                    // Load configurator script
                                                    let cfgEngineClass = window["hcs_cfg_engine_" + cfgId];

                                                    if(typeof cfgEngineClass !== "undefined") {
                                                        window.cadclick.hcs[cfgId].cfgEngine = new cfgEngineClass.default(window.cadclick.hcs[cfgId]);

                                                        if(!self.constants.minified && Object.getPrototypeOf(window.cadclick.hcs[cfgId].cfgEngine.constructor).name !== "ConfiguratorBase") {
                                                            console.error(`Could not initialize '${cfgId}' configurator class: Class does not inherit from 'ConfiguratorBase'!`);
                                                        }
                                                    } else {
                                                        console.warn(`No configuration engine class found for configurator '${cfgId}'`);
                                                    }

                                                    switch((window.cadclick.hcs[cfgId].settings.get("SETTINGS.TRACKING_ENGINE") || "").toLowerCase()) {
                                                        case "googleanalytics":
                                                            window.cadclick.hcs[cfgId].tracker = new GoogleAnalytics({
                                                                hcs: window.cadclick.hcs[cfgId]
                                                            });
                                                            break;

                                                        default:
                                                            // Set "dummy" tracker to avoid checking availability of "tracker" instance.
                                                            // So tracker can be called at any time with running into null references.
                                                            window.cadclick.hcs[cfgId].tracker = new TrackingBase({
                                                                hcs: window.cadclick.hcs[cfgId]
                                                            });
                                                            break;
                                                    }

                                                    window.cadclick.hcs[cfgId].partsList = new HcsPartsList({
                                                        hcs: window.cadclick.hcs[cfgId]
                                                    });

                                                    window.cadclick.hcs[cfgId].cart = new HcsCart({
                                                        hcs: window.cadclick.hcs[cfgId],
                                                        currency: opts.currency,
                                                        availablePriceTypes: opts.availablePriceTypes,
                                                        selectedPriceType: opts.selectedPriceType
                                                    });

                                                    window.cadclick.hcs[cfgId].ui.init();

                                                    // Disable instance move in ccAO to remove the X cursor
                                                    // when the user clicks a button inside the toolbar.
                                                    window.cadclick.hcs[cfgId].ui.toolbar.toolbarButtonClicked((event) => {
                                                        window.cadclick.hcs[cfgId].cad.ccAO.Snap.resetInteraction("move");
                                                    });

                                                    window.cadclick.hcs[cfgId].api.checkConfigurationExists(opts.configurationId, function(cfgExistsResult) {
                                                        window.cadclick.debug.console.debug("Storage state:" +
                                                            window.cadclick.debug.console.table(window.cadclick.hcs[cfgId].api.configStorage("debug-info"))
                                                        );

                                                        window.cadclick.debug.console.debug("Check configuration exists: " + opts.configurationId +
                                                            window.cadclick.debug.console.table([
                                                                { "Exists:": cfgExistsResult.exists },
                                                                { "Configuration ID:": cfgExistsResult.configurationId },
                                                                { "ID type:": window.cadclick.hcs[cfgId].api.enumValue(self.constants.enums.configurationIdTypes, cfgExistsResult.configurationIdType) },
                                                                { "Cfg state:": window.cadclick.hcs[cfgId].api.enumValue(self.constants.enums.configurationStates, cfgExistsResult.configurationState) },
                                                                { "hcs-force-load:": loadCfgForce }
                                                            ])
                                                        );

                                                        // No configuration with given ID found
                                                        // In this case we show a message and do nothing else.
                                                        if(!cfgExistsResult.exists && urlCfgId) {
                                                            window.cadclick.hcs[cfgId].messageBox.show({
                                                                text: window.cadclick.hcs[cfgId].t.gui("LoadCfg_IdNotExist_Message").format(urlCfgId),
                                                                messageType: MessageBox.MB_WARN,
                                                                buttons: MessageBox.MB_NONE,
                                                                closeOnEscape: false
                                                            });
                                                        } else {
                                                            // Override given (external) configuration ID
                                                            // This is necessary when a short ID is given, then the "exists"
                                                            // functions gets us the real configuration ID so we have to work
                                                            // with this instead the short ID. Because we don't want the short ID
                                                            // to be written to the "CONFIGURATION_ID" field in database.
                                                            opts.configurationId = cfgExistsResult.configurationId;

                                                            switch(self.constants.enums.configurationIdTypes[opts.configurationIdType]) {
                                                                case self.constants.enums.configurationIdTypes.Shop:
                                                                    window.cadclick.debug.console.debug("Load configuration: Open existing configuration from within shop/favorites.");

                                                                    window.cadclick.hcs[cfgId].api.loadConfiguration(opts.configurationId, false);
                                                                    break;

                                                                default:
                                                                    switch(cfgExistsResult.configurationIdType) {
                                                                        // "Default": Saved entry in DB does neither contain a short ID, nor a shop ID.
                                                                        case self.constants.enums.configurationIdTypes.Default:
                                                                            if(!loadCfgForce) {
                                                                                let storageCfgState = window.cadclick.hcs[cfgId].api.configStorage();

                                                                                if(storageCfgState.configurationId === opts.configurationId) {
                                                                                    // Load configuration from existing user session (requires CFG state in local storage).
                                                                                    if(window.cadclick.hcs[cfgId].settings.get("SETTINGS.USER_CONFIGURATION_SESSION_RELOAD", "bool") && storageCfgState.configured) {
                                                                                        window.cadclick.hcs[cfgId].api.loadConfigurationObject(storageCfgState.configurationId, true, function(cfgLoadResult) {
                                                                                            if(cfgLoadResult.success) {
                                                                                                if(cfgLoadResult.configurationId) {
                                                                                                    window.cadclick.hcs[cfgId].messageBox.show({
                                                                                                        text: window.cadclick.hcs[cfgId].t.gui("LoadStorageCfg_Message"),
                                                                                                        buttonTexts: {
                                                                                                            "yes": window.cadclick.hcs[cfgId].t.gui("LoadStorageCfg_ButtonLoad"),
                                                                                                            "no": window.cadclick.hcs[cfgId].t.gui("LoadStorageCfg_ButtonNew")
                                                                                                        },
                                                                                                        messageType: MessageBox.MB_QUESTION,
                                                                                                        buttons: MessageBox.MB_YES | MessageBox.MB_NO,
                                                                                                        onYes: function() {
                                                                                                            window.cadclick.debug.console.debug("Load configuration: Continue current configuration (because of page reload)");
                                                                                                            this.close();
                                                                                                            window.cadclick.hcs[cfgId].api.loadConfiguration(cfgLoadResult.configurationId, true);
                                                                                                        },
                                                                                                        onNo: function() {
                                                                                                            window.cadclick.debug.console.debug("Start new configuration: Don't continue existing configuration");
                                                                                                            this.close();
                                                                                                            window.cadclick.hcs[cfgId].api.startConfiguration({ showConfirmDialog: false });
                                                                                                        }
                                                                                                    });
                                                                                                } else {
                                                                                                    // User do not want to reload current configuration from storage.
                                                                                                    window.cadclick.hcs[cfgId].api.startConfiguration({ showConfirmDialog: false });
                                                                                                }
                                                                                            }
                                                                                        });
                                                                                    } else {
                                                                                        window.cadclick.debug.console.debug("Start new configuration: Configuration reload disabled or storage state is not 'configured'.");

                                                                                        // Restoring current configuration from storage is disabled
                                                                                        window.cadclick.hcs[cfgId].api.startConfiguration({
                                                                                            configurationId: opts.configurationId,
                                                                                            showConfirmDialog: false
                                                                                        });
                                                                                    }
                                                                                } else {
                                                                                    if(self.constants.enums.configurationIdTypes[opts.configurationIdType] === self.constants.enums.configurationIdTypes.New) {
                                                                                        window.cadclick.debug.console.debug("Start configuration: Storage configuration ID <> given configuration ID");

                                                                                        window.cadclick.hcs[cfgId].api.startConfiguration({
                                                                                            configurationId: opts.configurationId,
                                                                                            showConfirmDialog: false
                                                                                        });
                                                                                    } else {
                                                                                        window.cadclick.debug.console.debug("Load configuration: Storage configuration ID <> given configuration ID");

                                                                                        // Configuration ID to be loaded is different from ID in local stoarge
                                                                                        window.cadclick.hcs[cfgId].api.loadConfiguration(opts.configurationId, false);
                                                                                    }
                                                                                }
                                                                            } else {
                                                                                window.cadclick.debug.console.debug("Load configuration: Force loading configuration (hcs-force-load=1)");

                                                                                // User has passed a configuration ID and wants to load it from database and not from current session storage.
                                                                                // hcs-force-load == 1
                                                                                window.cadclick.hcs[cfgId].api.loadConfiguration(opts.configurationId, false);
                                                                            }
                                                                            break;

                                                                        default:
                                                                            window.cadclick.debug.console.debug("Load configuration: Either shop ID or short ID.");

                                                                            // When given ID is either a "shop" or "short" ID we load configuration immediately.
                                                                            window.cadclick.hcs[cfgId].api.loadConfiguration(opts.configurationId, false);
                                                                            break;
                                                                    }
                                                                    break;
                                                            }
                                                        }
                                                    });

                                                    opts.ready.call(window.cadclick.hcs[cfgId], {});
                                                },
                                                error: function(arg) {
                                                    container.innerHTML = self.createErrorHtml(`"Error loading script: ${arg.script}`);
                                                }
                                            });
                                        } else {
                                            delete window.cadclick.hcs[cfgId];

                                            container.innerHTML = self.createErrorHtml(inlineResult.error.message, inlineResult.error.details);
                                        }
                                    })
                                    /* from: fetch("Inline/HcS") */
                                    .catch(error => {
                                        delete window.cadclick.hcs[cfgId];

                                        container.innerHTML = self.createErrorHtml(error.message, error.stack);
                                    });
                            });
                    });
                })
                /* from: fetch("settings") */
                .catch(error => {
                    //container.innerHTML = self.createErrorHtml(`"Error loading configurator settings: ${error.message}`);
                    container.innerHTML = self.createErrorHtml(error.message);
                });
        } else {
            console.error("Could not initialize configuration suite: No container element found." + (typeof opts.container === "string" ? " Please check your 'container' selector!" : ""));
        }
    }
}

window.cadclick.loader = new HcsLoader();